import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, RefreshCcw, BarChart3, CreditCard, Users, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

interface ExtensionConfig {
  icon: React.ReactNode;
  emoji: string;
  titleKey: string;
  subtitleKey: string;
  features: string[];
}

const extensionConfigs: Record<string, ExtensionConfig> = {
  "self-service": {
    icon: <RefreshCcw className="h-8 w-8" />,
    emoji: "🔄",
    titleKey: "extpage.ss_title",
    subtitleKey: "extpage.ss_subtitle",
    features: [
      "extpage.ss_feat_cancel",
      "extpage.ss_feat_rebook",
      "extpage.ss_feat_deadline",
      "extpage.ss_feat_notifications",
      "extpage.ss_feat_shop",
      "extpage.ss_feat_email",
    ],
  },
  statistics: {
    icon: <BarChart3 className="h-8 w-8" />,
    emoji: "📊",
    titleKey: "extpage.stats_title",
    subtitleKey: "extpage.stats_subtitle",
    features: [
      "extpage.stats_feat_kpi",
      "extpage.stats_feat_charts",
      "extpage.stats_feat_revenue",
      "extpage.stats_feat_customers",
      "extpage.stats_feat_periods",
      "extpage.stats_feat_export",
    ],
  },
  "pay-at-order": {
    icon: <CreditCard className="h-8 w-8" />,
    emoji: "💳",
    titleKey: "extpage.pao_title",
    subtitleKey: "extpage.pao_subtitle",
    features: [
      "extpage.pao_feat_online",
      "extpage.pao_feat_mollie",
      "extpage.pao_feat_status",
      "extpage.pao_feat_webhook",
      "extpage.pao_feat_secure",
      "extpage.pao_feat_auto",
    ],
  },
  employees: {
    icon: <Users className="h-8 w-8" />,
    emoji: "👥",
    titleKey: "extpage.emp_title",
    subtitleKey: "extpage.emp_subtitle",
    features: [
      "extpage.emp_feat_assign",
      "extpage.emp_feat_hours",
      "extpage.emp_feat_services",
      "extpage.emp_feat_availability",
      "extpage.emp_feat_calendar",
      "extpage.emp_feat_preference",
    ],
  },
};

const ExtensionLandingPage = () => {
  const { extensionId } = useParams<{ extensionId: string }>();
  const { t } = useLanguage();

  const config = extensionId ? extensionConfigs[extensionId] : null;

  if (!config) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-hero pt-32 pb-20">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute top-1/4 left-1/4 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[120px]" />
            <div className="absolute bottom-1/4 right-1/4 h-[400px] w-[400px] rounded-full bg-accent/10 blur-[100px]" />
          </div>
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />

          <div className="container relative mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Link
                  to="/"
                  className="mb-6 inline-flex items-center gap-2 text-sm text-primary-foreground/60 hover:text-primary-foreground/80 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("extpage.back_home")}
                </Link>
              </motion.div>

              <motion.div
                className="mt-4 mb-6 flex justify-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary-foreground/10 text-4xl backdrop-blur-sm border border-primary-foreground/10">
                  {config.emoji}
                </div>
              </motion.div>

              <motion.h1
                className="text-3xl font-extrabold leading-tight tracking-tight text-primary-foreground sm:text-5xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                {t(config.titleKey)}
              </motion.h1>

              <motion.p
                className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/70"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 }}
              >
                {t(config.subtitleKey)}
              </motion.p>

              <motion.div
                className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.35 }}
              >
                <Button size="lg" className="h-12 px-8 text-base shadow-glow" asChild>
                  <Link to="/signup">
                    {t("landing.hero_cta")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              className="mx-auto max-w-3xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-bold text-foreground text-center mb-12">
                {t("extpage.features_title")}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {config.features.map((featKey, i) => (
                  <motion.div
                    key={featKey}
                    className="flex items-start gap-3 rounded-xl border border-border bg-card p-5 shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                    <span className="text-sm text-card-foreground leading-relaxed">{t(featKey)}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-24 bg-gradient-hero overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
          <div className="container relative mx-auto px-4">
            <motion.div
              className="mx-auto max-w-2xl text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-4xl font-bold text-primary-foreground">
                {t("landing.cta_title")}
              </h2>
              <p className="mt-4 text-lg text-primary-foreground/70">
                {t("landing.cta_desc")}
              </p>
              <Button size="lg" className="mt-8 h-12 px-8 text-base shadow-glow" asChild>
                <Link to="/signup">
                  {t("landing.cta_button")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ExtensionLandingPage;
